import { useRef, useState, useEffect } from 'react';
import {
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverArrow,
  PopoverCloseButton,
  PopoverHeader,
  PopoverBody,
  PopoverFooter,
  Button,
  VStack,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  HStack,
  Text,
  Badge,
  useToast,
  Box,
  Portal,
  useDisclosure,
} from '@chakra-ui/react';
import { WarningIcon } from '@chakra-ui/icons';
import { Metric, MetricGoal } from '../../types';
import { isMetricOnTrack } from '../../utils/scorecardCalculations';

interface ScorecardCellEditorProps {
  metric: Metric;
  goal: MetricGoal | undefined;
  weekLabel: string;
  onSave: (value: number | null, notes: string) => Promise<void>;
  onDropToIssue: () => void;
  children: React.ReactNode;
}

export function ScorecardCellEditor({
  metric,
  goal,
  weekLabel,
  onSave,
  onDropToIssue,
  children,
}: ScorecardCellEditorProps) {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [value, setValue] = useState<string>('');
  const [notes, setNotes] = useState<string>('');
  const [saving, setSaving] = useState(false);
  const initialFocusRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isOpen) {
      // Initialize state when popover opens
      // Handle 0 value correctly (don't show empty string for 0)
      const actualVal = goal?.actualValue;
      setValue(actualVal !== undefined && actualVal !== null ? actualVal.toString() : '');
      setNotes(goal?.notes ?? '');
    }
  }, [isOpen, goal]);

  const handleSave = async () => {
    try {
      setSaving(true);
      const numValue = value === '' ? null : parseFloat(value);
      await onSave(numValue, notes);
      onClose();
    } catch (error) {
      console.error('Failed to save:', error);
    } finally {
      setSaving(false);
    }
  };

  const currentStatus = (() => {
    const val = value === '' ? null : parseFloat(value);
    // Use weekly goal if available, otherwise quarterly goal approximation or 0
    // Note: In a real app, weekly goal might be dynamically calculated if not explicit
    const target = metric.weeklyGoal ?? (metric.quarterlyGoal ? metric.quarterlyGoal : 0);
    
    // If we have a specific goal override for this week (custom goal), we'd use that here
    // For now, using the metric's standard goal
    const goalValue = goal?.goalValue ?? target;

    const onTrack = isMetricOnTrack(
      val,
      goalValue,
      metric.goalOrientation || 'gte',
      metric.goalRangeMin,
      metric.goalRangeMax
    );
    
    if (val === null) return 'no-data';
    return onTrack ? 'on-track' : 'off-track';
  })();

  return (
    <Popover
      isOpen={isOpen}
      onOpen={onOpen}
      onClose={onClose}
      initialFocusRef={initialFocusRef}
      placement="bottom"
      closeOnBlur={true}
      modifiers={[
        { name: 'flip', enabled: false },
        { name: 'preventOverflow', options: { padding: 8 } }
      ]}
    >
      <PopoverTrigger>
        {children}
      </PopoverTrigger>
      <Portal>
        <PopoverContent width="300px" boxShadow="xl" zIndex={9999} maxH="500px" overflowY="auto">
          <PopoverArrow />
          <PopoverCloseButton />
          <PopoverHeader fontWeight="bold" borderBottomWidth="1px">
            {weekLabel} Entry
          </PopoverHeader>
          <PopoverBody>
            <VStack spacing={4} align="stretch">
              <FormControl>
                <FormLabel fontSize="sm">Actual Value</FormLabel>
                <HStack>
                  <Input
                    ref={initialFocusRef}
                    value={value}
                    onChange={(e) => setValue(e.target.value)}
                    placeholder="Enter value"
                    type="number"
                    step="any"
                  />
                  
                  {currentStatus === 'off-track' && (
                    <Badge colorScheme="red" variant="solid" px={2} py={1} borderRadius="md">
                      Off Track
                    </Badge>
                  )}
                  {currentStatus === 'on-track' && (
                    <Badge colorScheme="green" variant="solid" px={2} py={1} borderRadius="md">
                      On Track
                    </Badge>
                  )}
                </HStack>
              </FormControl>

              <FormControl>
                <FormLabel fontSize="sm">Notes / Context</FormLabel>
                <Textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Explain why off-track or any other context..."
                  size="sm"
                  resize="vertical"
                />
              </FormControl>

              {currentStatus === 'off-track' && (
                <Box bg="orange.50" p={2} borderRadius="md" borderWidth="1px" borderColor="orange.200">
                  <HStack spacing={2} align="start">
                    <WarningIcon color="orange.500" mt={1} />
                    <VStack align="start" spacing={1}>
                      <Text fontSize="xs" fontWeight="bold" color="orange.800">
                        Issue Detected
                      </Text>
                      <Text fontSize="xs" color="orange.700" whiteSpace="normal">
                        This metric is off-track. Consider adding it to the Issues List.
                      </Text>
                    </VStack>
                  </HStack>
                  <Button
                    size="xs"
                    colorScheme="orange"
                    variant="outline"
                    width="full"
                    mt={2}
                    onClick={() => {
                      console.log('Drop to Issues button clicked');
                      onDropToIssue();
                    }}
                  >
                    Drop to Issues
                  </Button>
                </Box>
              )}
            </VStack>
          </PopoverBody>
          <PopoverFooter display="flex" justifyContent="flex-end" borderTopWidth="1px">
             <Button variant="ghost" mr={3} onClick={onClose} size="sm">
              Cancel
            </Button>
            <Button colorScheme="blue" onClick={handleSave} isLoading={saving} size="sm">
              Save
            </Button>
          </PopoverFooter>
        </PopoverContent>
      </Portal>
    </Popover>
  );
}
